'use client';

/**
 * RotatingText — a single line of text that animates when it changes: the outgoing line slides out
 * and fades while the incoming line arrives from the opposite edge (a vertical "conveyor").
 *
 * Motion is lazy-loaded via `<LazyMotion>` (the `domAnimation` bundle), the same pattern Aura's Tabs
 * and Suggestion use, so the animation code isn't in the initial bundle. Respects reduced-motion.
 *
 * Built from two permanently-mounted layers driven only by their `animate` target — no mount
 * animation, no `AnimatePresence` removal (both proved flaky under lazy motion: mount animations get
 * skipped and exited nodes never unmount). Layers alternate: the one showing the current text sits
 * centred; the other parks just above, invisible, ready to slide in.
 *
 * Text is left-aligned and the box left edge is fixed, so a shorter/longer line grows to the right
 * only — it never re-centres and jumps. Pass `reserveFor` (all the lines it will ever show) to also
 * pin the width to the widest line, so a neighbour (e.g. a timer) to its right can't shift either.
 */

import { LazyMotion, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { loadDomAnimation } from '../../../lib/motion';
import { cn } from '../../../lib/utils/cn';

// useLayoutEffect warns during SSR; fall back to useEffect there (measurement is client-only anyway).
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export interface RotatingTextProps {
  /** The line to show. When it changes, the old line animates out and the new one animates in. */
  text: string;
  /** Applied to each line — use it for the text styling (size, colour, gradient, shimmer). */
  className?: string;
  /** Swap duration in seconds. */
  duration?: number;
  /** Travel of the outgoing line. 'down' (default): old exits downward, new enters from the top. */
  direction?: 'up' | 'down';
  /**
   * Every line this may ever show. When set, the box reserves the widest line's width (measured with
   * the real font) so it never reflows — anything sitting to its right stays put. Omit for freeform /
   * translated copy where the full set isn't known; the left edge is still fixed either way.
   */
  reserveFor?: string[];
}

type Phase = 'in' | 'out' | 'ready';
type LayerKey = 'a' | 'b';
interface Layers {
  current: LayerKey;
  a: { text: string; phase: Phase };
  b: { text: string; phase: Phase };
}

export const RotatingText = ({
  text,
  className,
  duration = 0.45,
  direction = 'down',
  reserveFor,
}: RotatingTextProps) => {
  const reduce = useReducedMotion();
  const enterY = reduce ? '0%' : direction === 'down' ? '-100%' : '100%';
  const exitY = reduce ? '0%' : direction === 'down' ? '100%' : '-100%';

  const [layers, setLayers] = useState<Layers>({
    current: 'a',
    a: { text, phase: 'in' },
    b: { text: '', phase: 'ready' },
  });

  // New text → hand it to the parked layer, slide the current one out.
  useEffect(() => {
    setLayers((s) => {
      if (s[s.current].text === text) return s;
      return s.current === 'a'
        ? { current: 'b', a: { ...s.a, phase: 'out' }, b: { text, phase: 'in' } }
        : { current: 'a', a: { text, phase: 'in' }, b: { ...s.b, phase: 'out' } };
    });
  }, [text]);

  // Once a layer has slid out, park it back above (invisibly) so it's ready to enter next time.
  useEffect(() => {
    const outKey: LayerKey | null =
      layers.a.phase === 'out' ? 'a' : layers.b.phase === 'out' ? 'b' : null;
    if (!outKey) return;
    const timer = window.setTimeout(
      () =>
        setLayers((s) =>
          s[outKey].phase === 'out' ? { ...s, [outKey]: { ...s[outKey], phase: 'ready' } } : s,
        ),
      (reduce ? 150 : duration * 1000) + 40,
    );
    return () => window.clearTimeout(timer);
  }, [layers, duration, reduce]);

  // Measure the widest reserved line and pin the box to it (the shrink-wrapped ruler = the max).
  const rulerRef = useRef<HTMLSpanElement>(null);
  const [reservedWidth, setReservedWidth] = useState<number>();
  const reserveKey = reserveFor?.join('\u0000');
  useIsoLayoutEffect(() => {
    if (!reserveFor?.length) {
      setReservedWidth(undefined);
      return;
    }
    const measure = () => {
      if (rulerRef.current) {
        setReservedWidth(Math.ceil(rulerRef.current.getBoundingClientRect().width));
      }
    };
    measure();
    // Re-measure once web fonts settle — the first pass can run against a narrower fallback font.
    let cancelled = false;
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        if (!cancelled) measure();
      });
    }
    return () => {
      cancelled = true;
    };
  }, [reserveKey, className]);

  const target = (phase: Phase) =>
    phase === 'in'
      ? { opacity: 1, y: '0%' }
      : phase === 'out'
        ? { opacity: 0, y: exitY }
        : { opacity: 0, y: enterY };

  const renderLayer = (key: LayerKey) => {
    const { text: lineText, phase } = layers[key];
    return (
      <m.span
        key={key}
        aria-hidden={phase !== 'in'}
        className={cn('col-start-1 row-start-1 text-left whitespace-nowrap', className)}
        initial={false}
        animate={target(phase)}
        transition={
          phase === 'ready'
            ? { duration: 0 }
            : { duration: reduce ? 0.15 : duration, ease: 'easeInOut' }
        }>
        {lineText}
      </m.span>
    );
  };

  return (
    <LazyMotion features={loadDomAnimation} strict>
      {/* One grid cell both layers share → auto-sizes (no magic height) and lets the outgoing +
          incoming lines overlap. overflow-hidden clips the vertical travel to a clean band. */}
      <span
        className="relative inline-grid justify-items-start overflow-hidden text-left align-bottom"
        style={reservedWidth ? { minWidth: reservedWidth } : undefined}>
        {renderLayer('a')}
        {renderLayer('b')}
        {reserveFor?.length ? (
          // Invisible ruler: shrink-wraps to the widest line, so its own width IS the max.
          <span
            ref={rulerRef}
            aria-hidden
            className="pointer-events-none invisible absolute top-0 left-0 w-max">
            {reserveFor.map((line, i) => (
              <span key={i} className={cn('block w-fit whitespace-nowrap', className)}>
                {line}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </LazyMotion>
  );
};
